import { Stack, Text } from '@chakra-ui/react';
import * as React from 'react';

import {
  EAttributeAllowedEditorKind,
  EAttributeAllowedViewerKind,
  EAttributeOwnerKind,
} from '@/graphql-types/globalTypes';

import {
  ALLOWED_EDITORS_LEVEL,
  allowedEditorsValueToLevel,
} from './AllowedEditorSelect';
import {
  allowedViewersLevelToIcon,
  allowedViewersValueToLevel,
} from './AllowedViewersSelect';

export const AllowedViewers: React.FC<{
  allowedViewers: EAttributeAllowedViewerKind[];
  allowedEditors: EAttributeAllowedEditorKind[];
  ownerKind: EAttributeOwnerKind;
  isViewingOwnProfile: boolean;
  canCurrentUserEditValue: boolean;
}> = ({
  allowedViewers,
  ownerKind,
  isViewingOwnProfile,
  allowedEditors,
  canCurrentUserEditValue,
}) => {
  const allowedEditorsLevel = allowedEditorsValueToLevel(allowedEditors);

  const label = React.useMemo(() => {
    if (allowedViewers.includes(EAttributeAllowedViewerKind.GUEST)) {
      return 'Visible to Anyone';
    }

    if (allowedViewers.includes(EAttributeAllowedViewerKind.LOGGED_IN_USER)) {
      return 'Visible to Logged In Users';
    }

    if (
      allowedViewers.includes(EAttributeAllowedViewerKind.ASSOCIATION_MEMBER)
    ) {
      if (isViewingOwnProfile) {
        return {
          [EAttributeOwnerKind.USER]: 'Visible to Members',
          [EAttributeOwnerKind.ORGANIZATION]: 'Visible to Members',
        }[ownerKind];
      }

      return {
        [EAttributeOwnerKind.USER]: 'Visible to Members & the User',
        [EAttributeOwnerKind.ORGANIZATION]:
          "Visible to Members & the Company's Admins",
      }[ownerKind];
    }

    if (allowedViewers.includes(EAttributeAllowedViewerKind.SELF)) {
      if (isViewingOwnProfile) {
        return {
          [EAttributeOwnerKind.USER]: 'Only Visible to Me & Admins',
          [EAttributeOwnerKind.ORGANIZATION]: `Only Visible to My Company's Admins & Association Admins`,
        }[ownerKind];
      }

      return {
        [EAttributeOwnerKind.USER]: 'Visible to the User',
        [EAttributeOwnerKind.ORGANIZATION]: "Visible to the Company's Admins",
      }[ownerKind];
    }

    return {
      [EAttributeOwnerKind.USER]: 'Only visible to Admins',
      [EAttributeOwnerKind.ORGANIZATION]: 'Only visible to Association Admins',
    }[ownerKind];
  }, [allowedViewers, isViewingOwnProfile, ownerKind]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      textStyle="compact"
      color="gray.600"
      spacing="1"
      pt="1"
    >
      {allowedViewersLevelToIcon(allowedViewersValueToLevel(allowedViewers))}
      <Text>
        {label}
        {isViewingOwnProfile &&
        !canCurrentUserEditValue &&
        allowedEditorsLevel === ALLOWED_EDITORS_LEVEL.ADMINS_ONLY
          ? ' • Contact an Association Admin to edit this field'
          : ''}
      </Text>
    </Stack>
  );
};
